import { Injectable } from '@angular/core'
import { LocalStorageService } from './local-storage-service'
import { Subject } from 'rxjs'

export class TokenPair {
    constructor(
        public accessToken: string | null,
        public refreshToken: string | null
    ) { }

    public hasValidTokens = () => {
        return this.accessToken !== null && this.refreshToken !== null
    }
}

@Injectable({
    providedIn: 'root',
})
export class TokenRepositoryService {

    private static readonly EXPIRING_SOON_SECONDS = 60

    private readonly ACCESS_TOKEN_KEY = 'accessToken'
    private readonly REFRESH_TOKEN_KEY = 'refreshToken'
    private tokens: Subject<TokenPair> = new Subject()

    constructor(
        private localStorageService: LocalStorageService
    ) { }

    getAccessToken(): string | null {
        const token = this.localStorageService.getItem(this.ACCESS_TOKEN_KEY) as string
        return token === '' ? null : token
    }

    saveAccessToken(token: string) {
        this.localStorageService.setItem(this.ACCESS_TOKEN_KEY, token)
        this.tokens.next(new TokenPair(
            token,
            this.getRefreshToken()
        ))
    }

    getRefreshToken(): string | null {
        const token = this.localStorageService.getItem(this.REFRESH_TOKEN_KEY) as string
        return token === '' ? null : token
    }

    saveRefreshToken(token: string) {
        this.localStorageService.setItem(this.REFRESH_TOKEN_KEY, token)
        this.tokens.next(new TokenPair(
            this.getAccessToken(),
            token
        ))
    }

    removeTokens() {
        this.localStorageService.removeItem(this.ACCESS_TOKEN_KEY)
        this.localStorageService.removeItem(this.REFRESH_TOKEN_KEY)
        this.tokens.next(new TokenPair(null, null))
    }

    hasTokens() {
        return this.getAccessToken() !== null && this.getRefreshToken() !== null
    }

    accessTokenExpiresSoon(): boolean {
        const token = this.getAccessToken()
        const encodedClaims = token?.split('.')[1]
        if (encodedClaims === undefined) {
            return false
        }
        try {
            const claims = JSON.parse(this.decodeBase64Url(encodedClaims)) as { exp?: unknown }
            return typeof claims.exp === 'number'
                && claims.exp <= Math.floor(Date.now() / 1000) + TokenRepositoryService.EXPIRING_SOON_SECONDS
        } catch {
            return false
        }
    }

    private decodeBase64Url(value: string): string {
        const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
        return atob(padded)
    }
}
